import { createRouter, createWebHistory } from 'vue-router';
import AccountSettingsPage from './pages/AccountSettingsPage.vue';
import ChapterRoastPage from './pages/ChapterRoastPage.vue';
import AdminChapterRoastsPage from './pages/AdminChapterRoastsPage.vue';
import AdminConsultsPage from './pages/AdminConsultsPage.vue';
import AuthorNationPage from './pages/AuthorNationPage.vue';
import ConsultBookingPage from './pages/ConsultBookingPage.vue';
import ConsultBookPage from './pages/ConsultBookPage.vue';
import AdminFeedbackPage from './pages/AdminFeedbackPage.vue';
import AdminSearchPage from './pages/AdminSearchPage.vue';
import AdminUserPage from './pages/AdminUserPage.vue';
import AuthWizardPage from './pages/AuthWizardPage.vue';
import BenchmarkPage from './pages/BenchmarkPage.vue';
import BillingPage from './pages/BillingPage.vue';
import CheckoutFlowPage from './pages/CheckoutFlowPage.vue';
import CreditsPage from './pages/CreditsPage.vue';
import MyEditsPage from './pages/MyEditsPage.vue';
import NotFoundPage from './pages/NotFoundPage.vue';
import ProjectPage from './pages/ProjectPage.vue';
import TPage from './pages/TPage.vue';
import { useAuthStore } from './stores/auth';

const routes = [
  {
    path: '/auth',
    name: 'auth',
    component: AuthWizardPage,
    meta: { requiresAuth: false },
  },
  {
    path: '/edits',
    name: 'edits',
    component: MyEditsPage,
    meta: { requiresAuth: true },
  },
  {
    path: '/account',
    name: 'account',
    component: AccountSettingsPage,
    meta: { requiresAuth: true },
  },
  {
    path: '/billing',
    name: 'billing',
    component: BillingPage,
    meta: { requiresAuth: true },
  },
  {
    path: '/credits',
    name: 'credits',
    component: CreditsPage,
    meta: { requiresAuth: true },
  },
  {
    path: '/benchmark',
    name: 'benchmark',
    component: BenchmarkPage,
    meta: { requiresAuth: true },
  },
  {
    path: '/admin/users/:uid',
    name: 'admin-user',
    component: AdminUserPage,
    props: true,
    meta: { requiresAuth: true },
  },
  {
    path: '/admin/users',
    name: 'admin-search',
    component: AdminSearchPage,
    meta: { requiresAuth: true },
  },
  {
    path: '/admin/feedback',
    name: 'admin-feedback',
    component: AdminFeedbackPage,
    meta: { requiresAuth: true },
  },
  {
    path: '/admin/chapter-roasts',
    name: 'admin-chapter-roasts',
    component: AdminChapterRoastsPage,
    meta: { requiresAuth: true },
  },
  {
    path: '/admin/consults',
    name: 'admin-consults',
    component: AdminConsultsPage,
    meta: { requiresAuth: true },
  },
  // Author Nation booth consults. Booking pages handle their own sign-in, since manage
  // links from emails carry a token instead.
  {
    path: '/author-nation',
    name: 'author-nation',
    component: AuthorNationPage,
    meta: { requiresAuth: false },
  },
  {
    path: '/author-nation/book',
    name: 'consult-book',
    component: ConsultBookPage,
    meta: { requiresAuth: false },
  },
  {
    path: '/author-nation/booking/:id',
    name: 'consult-booking',
    component: ConsultBookingPage,
    meta: { requiresAuth: false },
  },
  {
    path: '/t',
    name: 't',
    component: TPage,
    meta: { requiresAuth: false },
  },
  {
    path: '/chapter-roast',
    name: 'chapter-roast',
    component: ChapterRoastPage,
    meta: { requiresAuth: false },
  },
  {
    path: '/checkout/:projectId',
    name: 'checkout',
    component: CheckoutFlowPage,
    meta: { requiresAuth: true },
  },
  // Dev-only fixture preview; unregistered in production builds. MODE (not
  // DEV) because `vite build --mode development` — the npm run dev:frontend
  // watch build — keeps NODE_ENV=production, so DEV is false there.
  ...(import.meta.env.MODE !== 'production'
    ? [
        {
          path: '/edit/dev-preview',
          name: 'dev-preview',
          component: () => import('./pages/DevPreviewPage.vue'),
          meta: { requiresAuth: false },
        },
      ]
    : []),
  {
    path: '/edit/:projectId/:frameworkId/:fileName',
    name: 'project-file-nested',
    component: ProjectPage,
    props: true,
  },
  {
    path: '/edit/:projectId/:outputName',
    name: 'project',
    component: ProjectPage,
    props: true,
    meta: { requiresAuth: false },
  },
  {
    path: '/edit/:projectId',
    name: 'project-root',
    component: ProjectPage,
    props: true,
    meta: { requiresAuth: false },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: NotFoundPage,
    meta: { requiresAuth: false },
  },
];

const router = createRouter({
  history: createWebHistory('/'),
  routes,
});

// Wait for auth to be ready before checking authentication
router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore();

  // Wait for Firebase auth to initialize
  if (authStore.loading) {
    // Create a watcher that waits for loading to complete
    await new Promise<void>(resolve => {
      const unwatch = authStore.$subscribe(
        () => {
          if (!authStore.loading) {
            unwatch();
            resolve();
          }
        },
        { flush: 'sync' }
      );
    });
  }

  // Check if route requires authentication
  const requiresAuth = to.meta['requiresAuth'] !== false;

  if (requiresAuth && !authStore.isAuthenticated) {
    // Redirect to auth page with return URL
    const query: Record<string, string> = {
      redirect: to.fullPath,
      ...(to.path.startsWith('/checkout/') && { context: 'edit' }),
    };

    next({ name: 'auth', query });
  } else if (to.name === 'auth' && authStore.isAuthenticated) {
    // If already authenticated and trying to access auth page, redirect to edits
    next({ name: 'edits' });
  } else {
    next();
  }
});

export default router;
